import { Component, OnInit } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RosterService } from '../../services/roster.service';
import { Roster } from '../../models/roster';
import { rosterProfileIconUrl } from '../../utils/roster-image';

@Component({
  selector: 'app-outreach',
  imports: [CommonModule, FormsModule, NgTemplateOutlet],
  templateUrl: './outreach.component.html',
  styleUrl: './outreach.component.css'
})
export class OutreachComponent implements OnInit {
  allRosters: Roster[] = [];
  selectedRosters: Roster[] = [];
  loading = true;
  failedPrimary = new Set<string>();  // tileImageUrl failed, try imageUrl
  failedAll = new Set<string>();      // both failed, show empty circle

  nameFilter = '';
  minAge = 0;
  maxAge = 70;
  rateMin = 0;
  rateMax = 50000;

  readonly AGE_STEP = 1;
  readonly AGE_MAX = 70;
  readonly RATE_STEP = 1000;
  readonly RATE_MAX = 50000;

  pitchTitle = 'For Your Campaign';
  pitchBody = 'By Vertical Talent';

  // Drag state
  dragRosterId: string | null = null;
  dragFromSelected = false;
  dragFromSelectedIndex = -1;
  dropTargetActive = false;
  dropIndex: number | null = null;

  constructor(private rosterService: RosterService) {}

  ngOnInit(): void {
    this.rosterService.getRosters().subscribe({
      next: rosters => { this.allRosters = rosters.sort((a, b) => a.name.localeCompare(b.name)); },
      complete: () => { this.loading = false; }
    });
  }

  getAgeDisplay(birthday: string): string {
    const age = this.getAge(birthday);
    return age !== null ? String(age) : 'Unknown';
  }

  getAge(birthday: string): number | null {
    if (!birthday) return null;
    const dob = new Date(birthday);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  }

  get filteredRosters(): Roster[] {
    const selectedIds = new Set(this.selectedRosters.map(r => r.id));
    return this.allRosters.filter(r => {
      if (selectedIds.has(r.id)) return false;
      if (this.nameFilter) {
        const q = this.nameFilter.toLowerCase();
        const matches = [r.name, r.tiktokHandle, r.instagramHandle, r.youtubeHandle]
          .some(v => v && v.toLowerCase().includes(q));
        if (!matches) return false;
      }
      if (this.exclusiveFilter === 'yes' && !r.exclusive) return false;
      if (this.exclusiveFilter === 'no' && r.exclusive) return false;
      if (this.activeFilter === 'yes' && !r.active) return false;
      if (this.activeFilter === 'no' && r.active) return false;
      if (this.minAge > 0 || this.maxAge < this.AGE_MAX) {
        const age = this.getAge(r.birthday);
        if (age !== null && (age < this.minAge || age > this.maxAge)) return false;
      }
      const rate = r.tiktokVideoBaseRate ?? 0;
      if (rate < this.rateMin || rate > this.rateMax) return false;

      if (this.selectedGenres.size > 0) {
        const rosterGenres = (r.genre || '').split('\n').map(g => g.trim()).filter(g => g);
        if (!rosterGenres.some(g => this.selectedGenres.has(g))) return false;
      }

      return true;
    });
  }

  exclusiveFilter: 'both' | 'yes' | 'no' = 'both';
  activeFilter: 'both' | 'yes' | 'no' = 'both';
  selectedGenres = new Set<string>();

  get allGenres(): string[] {
    const genres = new Set<string>();
    for (const r of this.allRosters) {
      if (r.genre) {
        r.genre.split('\n').map(g => g.trim()).filter(g => g).forEach(g => genres.add(g));
      }
    }
    return [...genres].sort();
  }

  toggleGenre(genre: string): void {
    if (this.selectedGenres.has(genre)) {
      this.selectedGenres.delete(genre);
    } else {
      this.selectedGenres.add(genre);
    }
  }

  get isAgeFilterActive(): boolean { return this.minAge > 0 || this.maxAge < this.AGE_MAX; }
  get isRateFilterActive(): boolean { return this.rateMin > 0 || this.rateMax < this.RATE_MAX; }

  get ageMinPct(): number { return (this.minAge / this.AGE_MAX) * 100; }
  get ageMaxPct(): number { return (this.maxAge / this.AGE_MAX) * 100; }
  get rateMinPct(): number { return (this.rateMin / this.RATE_MAX) * 100; }
  get rateMaxPct(): number { return (this.rateMax / this.RATE_MAX) * 100; }

  onAgeMinChange(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.minAge = Math.min(val, this.maxAge - this.AGE_STEP);
  }

  onAgeMaxChange(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.maxAge = Math.max(val, this.minAge + this.AGE_STEP);
  }

  onRateMinChange(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.rateMin = Math.min(val, this.rateMax - this.RATE_STEP);
  }

  onRateMaxChange(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.rateMax = Math.max(val, this.rateMin + this.RATE_STEP);
  }

  // ── Filtered column drag ──
  onFilteredDragStart(event: DragEvent, roster: Roster): void {
    this.dragRosterId = roster.id;
    this.dragFromSelected = false;
    this.dragFromSelectedIndex = -1;
    event.dataTransfer?.setData('text/plain', roster.id);
    event.dataTransfer!.effectAllowed = 'move';
  }

  // ── Selected column drag (reorder) ──
  onSelectedDragStart(event: DragEvent, roster: Roster, index: number): void {
    this.dragRosterId = roster.id;
    this.dragFromSelected = true;
    this.dragFromSelectedIndex = index;
    event.dataTransfer?.setData('text/plain', roster.id);
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragEnd(): void {
    this.resetDragState();
  }

  // ── Selected drop zone ──
  onSelectedDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.dropTargetActive = true;
    this.dropIndex = this.computeDropIndex(event, event.currentTarget as HTMLElement);
  }

  onSelectedDragLeave(event: DragEvent): void {
    const container = event.currentTarget as HTMLElement;
    if (!container.contains(event.relatedTarget as Node)) {
      this.dropIndex = null;
      this.dropTargetActive = false;
    }
  }

  onSelectedDrop(event: DragEvent): void {
    event.preventDefault();
    const id = event.dataTransfer?.getData('text/plain');
    if (!id) { this.resetDragState(); return; }

    const roster = this.allRosters.find(r => r.id === id);
    if (!roster) { this.resetDragState(); return; }

    let insertAt = this.dropIndex ?? this.selectedRosters.length;

    if (this.dragFromSelected) {
      this.selectedRosters.splice(this.dragFromSelectedIndex, 1);
      if (this.dragFromSelectedIndex < insertAt) insertAt--;
    } else {
      const existing = this.selectedRosters.findIndex(r => r.id === id);
      if (existing >= 0) this.selectedRosters.splice(existing, 1);
    }

    this.selectedRosters.splice(insertAt, 0, roster);
    this.resetDragState();
  }

  private computeDropIndex(event: DragEvent, container: HTMLElement): number {
    // Query only actual roster tiles — not the placeholder div
    const cards = Array.from(container.querySelectorAll('.selected-tile')) as HTMLElement[];
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      // Above the vertical center of this card → insert before it
      if (event.clientY < rect.top + rect.height / 2) return i;
      // Same row, left of horizontal center → insert before it
      if (event.clientY < rect.bottom && event.clientX < rect.left + rect.width / 2) return i;
    }
    return cards.length;
  }

  private resetDragState(): void {
    this.dragRosterId = null;
    this.dragFromSelected = false;
    this.dragFromSelectedIndex = -1;
    this.dropIndex = null;
    this.dropTargetActive = false;
  }

  selectAll(): void {
    const selectedIds = new Set(this.selectedRosters.map(r => r.id));
    for (const roster of this.filteredRosters) {
      if (!selectedIds.has(roster.id)) {
        this.selectedRosters.push(roster);
        selectedIds.add(roster.id);
      }
    }
  }

  openPitchPage(): void {
    const ids = this.selectedRosters.map(r => r.id).join(',');
    const params = new URLSearchParams({ ids });
    if (this.pitchTitle) params.set('title', this.pitchTitle);
    if (this.pitchBody) params.set('body', this.pitchBody);
    window.open(`/creators?${params.toString()}`, '_blank');
  }

  removeSelected(id: string): void {
    this.selectedRosters = this.selectedRosters.filter(r => r.id !== id);
  }

  imgSrc(roster: Roster): string | null {
    if (this.failedAll.has(roster.id)) return null;
    if (this.failedPrimary.has(roster.id)) return roster.imageUrl || null;
    return rosterProfileIconUrl(roster.id);
  }

  onImgError(event: Event, roster: Roster): void {
    if (!this.failedPrimary.has(roster.id) && roster.imageUrl) {
      // convention-based profile icon failed — fall back to imageUrl
      this.failedPrimary.add(roster.id);
      (event.target as HTMLImageElement).src = roster.imageUrl;
    } else {
      // imageUrl also failed (or there was none)
      this.failedAll.add(roster.id);
    }
  }
}
